import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { IconField } from '@/components/admin/shared/icon-field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { StudyPlan, StudyPlanFormData } from './hooks/use-study-plans'

/**
 * The create/edit form for a study plan — name, description, icon, URL.
 *
 * Shared by the coding and system-design study-plan sections, which had this
 * form twice: same state, same escape handler, same required-name check, same
 * markup, differing only in two placeholders and in whether the icon and URL
 * can be inherited from a parent source. Both copies had already drifted in
 * formatting, and a fix to one would not have reached the other.
 */
export function StudyPlanFormModal({
  isOpen,
  studyPlan,
  onClose,
  onSubmit,
  isSubmitting,
  namePlaceholder,
  descriptionPlaceholder,
  defaultIcon,
  defaultUrl,
}: {
  isOpen: boolean
  studyPlan?: StudyPlan | null
  onClose: () => void
  onSubmit: (data: StudyPlanFormData) => Promise<void>
  isSubmitting: boolean
  namePlaceholder: string
  descriptionPlaceholder: string
  /**
   * Present when the plan sits under a source that supplies its own icon/URL,
   * which is the only behavioural difference between the two sections: the
   * field is then annotated as inheriting rather than being required.
   */
  defaultIcon?: string | null
  defaultUrl?: string | null
}) {
  const [formData, setFormData] = useState<StudyPlanFormData>({
    name: '',
    description: '',
    icon: '',
    url: '',
  })

  // Sync form data when studyPlan changes (for edit mode)
  useEffect(() => {
    setFormData({
      name: studyPlan?.name || '',
      description: studyPlan?.description || '',
      icon: studyPlan?.icon || '',
      url: studyPlan?.url || '',
    })
  }, [studyPlan])

  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      toast.error('Name is required')
      return
    }
    await onSubmit({
      ...formData,
      icon: formData.icon || null,
      url: formData.url || null,
    })
  }

  const inheritHint = (
    <span className="text-muted-foreground/70 ml-1 font-normal">
      (inherits from source if empty)
    </span>
  )

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      {/* Backdrop - closes modal on click */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="bg-card border-border relative z-10 w-full max-w-md rounded-lg border p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-foreground text-lg font-semibold">
            {studyPlan ? 'Edit Study Plan' : 'Create Study Plan'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={namePlaceholder}
              required
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder={descriptionPlaceholder}
              className="border-input bg-background w-full rounded-md border p-3 text-sm"
              rows={3}
            />
          </div>
          <div>
            <IconField
              label={
                <>
                  Icon
                  {defaultIcon && inheritHint}
                </>
              }
              value={formData.icon || ''}
              onChange={(icon) => setFormData({ ...formData, icon })}
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              URL
              {defaultUrl && inheritHint}
            </label>
            <Input
              type="url"
              value={formData.url || ''}
              onChange={(e) =>
                setFormData({ ...formData, url: e.target.value })
              }
              placeholder="https://leetcode.com/studyplan/..."
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Link to the original study plan source
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : studyPlan ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
